import { faCheck, faCircleNotch } from '@fortawesome/pro-regular-svg-icons';
import { useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import styled, { css, keyframes } from 'styled-components';

import { FontAwesomeIcon } from 'atoms/icons';
import { Vertical } from 'atoms/layout/flex';

import { Input, Props as InputProps } from '../Input';

const Root = styled.div`
  flex: 1;
  min-width: 0;
  width: auto;
  display: inline-block;
  position: relative;

  & > input {
    width: 100%;
    padding-right: calc(5 * var(--unit) + var(--paddingLeft));
  }
`;
const Indicator = styled(Vertical).attrs({ center: true })`
  width: calc(5 * var(--unit));
  aspect-ratio: 1;
  position: absolute;
  inset: 0 0 0 auto;
`;

enum ProcessingState {
  Idle,
  Processing,
  Saved,
}

type Props = InputProps & {
  onDebouncedChange: (value: InputProps['value']) => Promise<void>;
  debounceDelay?: number;
};

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
`;

const beat = keyframes`
  0%,
  100% {
    opacity:  0.4;
    transform: scale(1);
  }

  50% {
    opacity: 1;
    transform: scale(1.125);
  }
`;

const StyledIcon = styled(FontAwesomeIcon)<{ $state: ProcessingState }>`
  ${({ $state }) =>
    // eslint-disable-next-line no-nested-ternary
    $state === ProcessingState.Processing
      ? css`
          animation: ${spin} 2s linear 0 infinite normal;
        `
      : $state === ProcessingState.Saved
        ? css`
            animation: ${beat} 1s cubic-bezier(0.4, 0, 0.6, 1) 0 infinite normal;
          `
        : null}
  @media (prefers-reduced-motion: reduce) {
    & {
      animation-delay: -1ms;
      animation-duration: 1ms;
      animation-iteration-count: 1;
      transition-delay: 0s;
      transition-duration: 0s;
    }
  }
`;

export const AutoSaveInput = ({
  onDebouncedChange,
  debounceDelay = 400,
  ...inputProps
}: Props) => {
  const isFirstRender = useRef(true);
  const [processingState, setProcessingState] = useState<ProcessingState>(
    ProcessingState.Idle
  );
  const [debouncedValue, setDebouncedValue] = useState<InputProps['value']>(
    inputProps.defaultValue || inputProps.value
  );

  useDebounce(
    async () => {
      if (debouncedValue && !isFirstRender.current) {
        setProcessingState(ProcessingState.Processing);
        await onDebouncedChange(debouncedValue);
        setProcessingState(ProcessingState.Saved);
        setTimeout(() => {
          setProcessingState(ProcessingState.Idle);
        }, 1000);
      }
      isFirstRender.current = false;
    },
    debounceDelay,
    [debouncedValue]
  );

  return (
    <Root>
      <Input
        onInput={event => {
          setDebouncedValue((event.target as HTMLInputElement).value);
          return inputProps.onInput?.(event);
        }}
        {...inputProps}
      />
      <Indicator>
        {[ProcessingState.Processing, ProcessingState.Saved].includes(
          processingState
        ) && (
          <StyledIcon
            icon={
              processingState === ProcessingState.Processing
                ? faCircleNotch
                : faCheck
            }
            color={
              processingState === ProcessingState.Processing
                ? 'var(--c-neutral-600)'
                : 'var(--c-green-600)'
            }
            $state={processingState}
          />
        )}
      </Indicator>
    </Root>
  );
};
